import * as React from 'react';
import { useEffect, useRef } from 'react';
import type { FetchResult } from 'react-apollo';

import user from 'js/lib/user';

import { Button, InlineNotification } from '@coursera/cds-core';
import type { DismissCalendarItemResponse } from '@coursera/grpc-types-degreehome/coursera/proto/degreehome/v1beta1/degree_home_calendar_api';

import type { FilterOptionValuesType } from 'bundles/degree-home/components/v2/Calendar';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { WithDismissCalendarItemMutation } from 'bundles/degree-home/components/v2/Calendar';
import { getFilterLabel } from 'bundles/degree-home/components/v2/Calendar/CalendarFilter';
import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';

import _t from 'i18n!nls/degree-home';

export const DismissWarningNotificationContent = ({
  loading,
  dismissAction,
  onHide,
  onCancel,
  onError,
  itemFilterType,
}: {
  loading: boolean;
  dismissAction: () => Promise<void | FetchResult<DismissCalendarItemResponse>>;
  onHide: () => void;
  onCancel: () => void;
  onError: () => void;
  itemFilterType: FilterOptionValuesType;
}) => {
  const { setRefetchRequired } = useDegreeContext();
  const notificationRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const hideButton = notificationRef.current?.querySelector<HTMLButtonElement>('#DismissWarningHideButton');
    hideButton?.focus();
  }, []);
  return (
    <InlineNotification
      primaryAction={
        <Button
          size="small"
          variant="primary"
          id="DismissWarningHideButton"
          disabled={loading}
          onClick={() => {
            dismissAction()
              .then((result) => {
                onHide();
                setRefetchRequired(true);
                return result;
              })
              .catch(() => {
                onError();
              });
          }}
        >
          {_t('Yes, hide')}
        </Button>
      }
      secondaryAction={
        <Button size="small" variant="ghost" onClick={onCancel}>
          {_t('Cancel')}
        </Button>
      }
      severity="warning"
      title={_t('Want to move this item to display on #{itemFilterType} only?', {
        itemFilterType: getFilterLabel(itemFilterType),
      })}
      ref={notificationRef}
    >
      {_t('Skipping this may have a significant impact on your final grade.')}
    </InlineNotification>
  );
};

export const DismissWarningNotification = ({
  courseId,
  itemId,
  onHide,
  onCancel,
  onError,
  itemFilterType,
}: {
  courseId: string;
  itemId: string;
  onHide: () => void;
  onCancel: () => void;
  onError: () => void;
  itemFilterType: FilterOptionValuesType;
}) => {
  const { degreeId } = useDegreeContext();

  return (
    <WithDismissCalendarItemMutation userId={user.get().id} degreeId={degreeId} courseId={courseId} itemId={itemId}>
      {({ dismissAction }, { loading }) => {
        return (
          <DismissWarningNotificationContent
            loading={loading}
            dismissAction={dismissAction}
            onHide={onHide}
            onCancel={onCancel}
            onError={onError}
            itemFilterType={itemFilterType}
          />
        );
      }}
    </WithDismissCalendarItemMutation>
  );
};

export const DismissSuccessNotification = ({
  onDismiss,
  itemFilterType,
}: {
  onDismiss: () => void;
  itemFilterType: FilterOptionValuesType;
}) => {
  const notificationRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const dismissButton = notificationRef.current?.querySelector<HTMLButtonElement>('[aria-label="Dismiss"]');
    dismissButton?.focus();
  }, []);

  return (
    <InlineNotification
      severity="success"
      title={_t('Your item has been moved')}
      onDismiss={onDismiss}
      ref={notificationRef}
    >
      {_t('You can still find this item by filtering for #{itemFilterType} in the dropdown.', {
        itemFilterType: getFilterLabel(itemFilterType),
      })}
    </InlineNotification>
  );
};

export const DismissErrorNotification = ({ onDismiss }: { onDismiss: () => void }) => {
  const notificationRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const dismissButton = notificationRef.current?.querySelector<HTMLButtonElement>('[aria-label="Dismiss"]');
    dismissButton?.focus();
  }, []);
  return (
    <InlineNotification
      severity="error"
      title={_t('Failed to move this item')}
      onDismiss={onDismiss}
      ref={notificationRef}
    >
      {_t('Something went wrong. Please try again later.')}
    </InlineNotification>
  );
};
